import * as yup from 'yup';
import { TeamInput } from '../../__generated__/graphql';

export interface TeamType {
  id: string;
}

export type TicketWithTeamFlags<T = object> = T & {
  id: string;
  allow_individuals: boolean;
  allow_create_team: boolean;
  allow_join_team: boolean;
  team_type?: TeamType | null;
};

export interface TeamFlags<TInput = object, TWithFlags = TicketWithTeamFlags<TInput>> {
  allowIndividuals: boolean;
  allowCreateTeam: boolean;
  allowJoinTeam: boolean;
  allowedTickets: TWithFlags[];
}

interface Registration {
  id: string;
  team?: {
    id: string;
  } | null;
}

const requiredIfPresent = (value: any) => typeof value === 'undefined' || value;

/**
 * Assumes that attributes are required when they are present.
 */
export const teamSchema = yup.object({
  team_type: yup.object({
    id: yup.string().required(),
  }),
  id: yup.string().test('required', '', requiredIfPresent),
  title: yup.string().test('required', '', requiredIfPresent),
  access_code: yup.string().test('required', '', requiredIfPresent),
}).nullable();

export const validateTeamPresence = (value: TeamInput | null, teamFlags: Omit<TeamFlags, 'allowedTickets'>) => {
  const { allowIndividuals, allowCreateTeam, allowJoinTeam } = teamFlags;

  if (value && !allowCreateTeam && !allowJoinTeam) {
    return false;
  }

  if (!value && !allowIndividuals) {
    return false;
  }

  return true;
};

export const getTeamSchema = (teamFlags: Omit<TeamFlags, 'allowedTickets'>) => (
  teamSchema.test('invalid', '', (value) => validateTeamPresence(value, teamFlags))
);

export const getTeamFlags = <T extends { id: string; } = { id: string; }>(
  teamType?: TeamType | null, selectedTickets: TicketWithTeamFlags<T>[] = [], registration?: Registration,
): TeamFlags<T> => {
  const allowedTickets = selectedTickets.filter((ticket) => (ticket.team_type?.id || null) === (teamType?.id || null));

  let allowIndividuals = true;
  let allowCreateTeam = false;
  let allowJoinTeam = false;
  let disallowJoinTeam = false;

  allowedTickets.forEach((ticket) => {
    if (!ticket.allow_individuals) {
      allowIndividuals = false;
    }

    if (ticket.allow_create_team) {
      allowCreateTeam = true;

      if (!ticket.allow_join_team) {
        disallowJoinTeam = true;
      }
    }

    if (ticket.allow_join_team) {
      allowJoinTeam = true;
    }
  });

  allowJoinTeam = (!disallowJoinTeam && allowJoinTeam);

  if (!allowCreateTeam && !allowJoinTeam && registration?.team) {
    allowJoinTeam = true;
  }

  return {
    allowIndividuals,
    allowCreateTeam,
    allowJoinTeam,
    allowedTickets,
  };
};
